import { useState, useRef } from "react";
import { View, ScrollView as RnScrollView } from "react-native";
import { Text } from "react-native-paper";
import { Controller, useForm } from "react-hook-form";
import { gql, useMutation } from "@apollo/client";

import Button from "../../components/Button";
import { useErrorScroll } from "../../components/ErrorMessage";
import TextInputArea from "../../components/TextInputArea";
import ReasonDropdown from "../../components/ReasonDropdown";
import useSnackBar from "../../hooks/useSnackBar";
import { KeyboardAvoidingView, ScrollView } from "../../components";
import Modal from "../../components/Modal";
import ActionsContainer from "../../components/ActionsContainer";

type FormData = {
  reason: string;
  message: string;
};

const defaultValues: FormData = {
  reason: "",
  message: "",
};

export const MUTATION = gql`
  mutation rejectDeferredCompSubmission($id: ID!, $message: String, $reason: String!) {
    rejectDeferredCompSubmission(id: $id, message: $message, reason: $reason) {
      message
      success
    }
  }
`;

const DEFAULT_ERROR = "An unknown error occurred while submitting this form. Please try again.";

type Props = {
  onSuccess(): void;
  visible: boolean;
  onDismiss(): void;
  id: string;
};

export default function ActivityReviewUnableToConfirmModal({ onSuccess, visible, onDismiss, id }: Props) {
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<FormData>({ defaultValues, mode: "onChange" });

  const [sendMutation] = useMutation(MUTATION);
  const [apiError, setApiError] = useState("");
  const { onSubmitError } = useErrorScroll({ defaultValues });
  const showSnackBar = useSnackBar();
  const scrollViewRef = useRef<RnScrollView>();

  const onPressSubmit = async ({ reason, message }: FormData) => {
    try {
      const {
        data: {
          rejectDeferredCompSubmission: { success, message: responseMessage },
        },
      } = await sendMutation({
        variables: {
          id,
          reason,
          message,
        },
      });

      if (success) {
        setTimeout(() => {
          showSnackBar({ message: "Submitted", icon: "check-circle", duration: 3000 });
        }, 1000);
        onSuccess();
      } else {
        setApiError(responseMessage || DEFAULT_ERROR);
      }
    } catch (e: any) {
      const message = e && e.message ? e.message : DEFAULT_ERROR;
      setApiError(message);
    }
  };

  return (
    <KeyboardAvoidingView isModalScreen>
      <Modal
        {...{
          visible,
          title: "Unable to Confirm",
        }}
      >
        <View className="bg-sky-white" testID="ActivityReviewUnableToConfirmModal">
          <ScrollView contentContainerStyle="grow" ref={scrollViewRef}>
            <View className="m-6 flex-1">
              <Text className="text-regular-normal font-400 text-ink-dark mb-6">
                Please select the reason you are unable to confirm the activity.
              </Text>
              <ReasonDropdown
                control={control}
                name="reason"
                label="Select Reason"
                reasons={["Participation level inaccurate", "Do not have enough information", "Other"]}
                rules={{ required: "required" }}
              />
              <Text className="text-regular-normal font-400 text-ink-dark mt-8 mb-6">
                (Optional) Message to requesting member. 500 character maximum.
              </Text>
              <Controller
                name="message"
                control={control}
                render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                  <TextInputArea
                    error={error}
                    onChangeText={onChange}
                    onBlur={onBlur}
                    value={value}
                    placeholder="Message"
                    onFocus={() => scrollViewRef.current?.scrollToEnd()}
                  />
                )}
              />
            </View>

            <ActionsContainer
              errorAlert
              errorMessage={apiError}
              onCloseErrorMessage={setApiError}
              horizontalStyle="all"
              insideModal
            >
              <Button onPress={onDismiss}>Cancel</Button>
              <Button
                loading={isSubmitting}
                disabled={isSubmitting}
                isValid={isValid}
                onPress={handleSubmit(onPressSubmit, onSubmitError)}
                mode="contained"
              >
                Submit
              </Button>
            </ActionsContainer>
          </ScrollView>
        </View>
      </Modal>
    </KeyboardAvoidingView>
  );
}
